import { useCallback, useEffect, useRef, useState } from 'react';
import { evaluationTableApi } from '../services/evaluationTableApi';
import { getApiErrorMessage } from '../utils/errorHandling';
import type { EvaluationType } from '../types/evaluations';
import type { EvaluationTableResponse } from '../types/evaluationTable';

interface UseEvaluationTableReturn {
  data: EvaluationTableResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

interface LoadedPeriod {
  evaluationType: EvaluationType;
  year: number;
  period: number;
  refreshKey: number;
}

/**
 * Laedt den kombinierten Tabellen-Vertrag (`GET /api/evaluations/table`) fuer einen Zeitraum.
 * Fehlen `year`/`period`, waehlt der Server selbst den letzten Zeitraum mit Daten (D-09).
 *
 * Etabliertes Fetch-Cancel-Refresh-Skelett dieses Projekts -- `cancelled`-Flag im Cleanup,
 * `refresh()` ueber einen `refreshKey`-Zaehler statt manuellem Neuladen.
 *
 * D-08: `MarketEvaluationPage` schreibt eine Antwort ohne angefragten Zeitraum per
 * `setSearchParams` in die URL, wodurch `year`/`period` als Props erneut ankommen -- diesmal
 * explizit gesetzt, aber mit denselben Werten, die `data` schon traegt. `lastLoadedRef` haelt den
 * zuletzt erfolgreich geladenen Zeitraum fest (aus der Antwort, nicht aus den Props -- ohne
 * Parameter waehlt der Server); stimmen die neuen Props exakt damit ueberein, wird der Abruf und
 * das Skeleton uebersprungen. `refresh()` erhoeht `refreshKey` bewusst und umgeht den Abgleich
 * dadurch gezielt, auch wenn `year`/`period` unveraendert bleiben.
 */
export function useEvaluationTable(
  evaluationType: EvaluationType,
  year?: number,
  period?: number
): UseEvaluationTableReturn {
  const [data, setData] = useState<EvaluationTableResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const lastLoadedRef = useRef<LoadedPeriod | null>(null);

  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const loaded = lastLoadedRef.current;
    if (
      year !== undefined &&
      period !== undefined &&
      loaded !== null &&
      loaded.evaluationType === evaluationType &&
      loaded.year === year &&
      loaded.period === period &&
      loaded.refreshKey === refreshKey
    ) {
      return;
    }

    // Ab hier laeuft ein neuer Abruf: der gemerkte Zeitraum gilt nicht mehr als angezeigt. Sonst
    // wuerde ein Rueckwechsel auf ihn, bevor dieser Abruf antwortet (abgebrochen, `finally` setzt
    // `loading` nicht zurueck), oben uebersprungen und das Skeleton bliebe stehen.
    lastLoadedRef.current = null;

    let cancelled = false;

    async function fetchTable() {
      setLoading(true);
      setError(null);

      try {
        const requestedPeriod =
          year !== undefined && period !== undefined
            ? { year, period }
            : undefined;
        const result = await evaluationTableApi.getTable(
          evaluationType,
          requestedPeriod
        );
        if (!cancelled) {
          setData(result);
          lastLoadedRef.current = {
            evaluationType: result.evaluationType,
            year: result.year,
            period: result.period,
            refreshKey,
          };
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            getApiErrorMessage(
              err,
              'Die Auswertung konnte nicht geladen werden.'
            )
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchTable();

    return () => {
      cancelled = true;
    };
  }, [evaluationType, year, period, refreshKey]);

  return { data, loading, error, refresh };
}
